import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import clsx from 'clsx';

/**
 * Floating quick-capture button + modal. Mounted globally in AppShell.
 *
 * Five capture types map to the recap form sections so an item logged in
 * the moment shows up immediately on this week's recap.
 */
const TYPES = [
  { id: 'voice',          label: 'Voice note',  icon: '🎙', color: 'bg-navy' },
  { id: 'photo',          label: 'Photo',       icon: '📷', color: 'bg-navy-600' },
  { id: 'wins',           label: 'Win',         icon: '🏆', color: 'bg-emerald-700' },
  { id: 'customer_challenges', label: 'Challenge', icon: '⚠️', color: 'bg-amber-600' },
  { id: 'new_opportunities',   label: 'Opportunity', icon: '💰', color: 'bg-crimson' },
  { id: 'high_alerts',    label: 'High alert',  icon: '🚨', color: 'bg-red-700' },
];

export default function QuickCapture() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null); // type id
  const [photoFiles, setPhotoFiles] = useState(null);
  const cameraRef = useRef(null);

  function pick(typeId) {
    if (typeId === 'voice') {
      setOpen(false);
      window.location.assign('/notes/new');
      return;
    }
    if (typeId === 'photo') {
      cameraRef.current?.click();
      return;
    }
    setActive(typeId);
  }

  function onCameraPick(e) {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setPhotoFiles(files);
      setActive('photo');
    }
    e.target.value = '';
  }

  return (
    <>
      <button
        aria-label="Quick capture"
        className="fixed right-4 bottom-20 md:bottom-6 z-30 w-14 h-14 rounded-full bg-crimson text-white text-3xl shadow-lg flex items-center justify-center hover:bg-crimson/90 active:scale-95 transition"
        onClick={() => setOpen(true)}
      >+</button>

      {open && !active && (
        <div className="fixed inset-0 z-40 bg-navy/40 flex items-end md:items-center justify-center p-4"
             onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-3 space-y-2 shadow-panel"
               onClick={e => e.stopPropagation()}>
            <div className="px-2 pt-1 pb-2 text-sm text-muted">Quick capture</div>
            {TYPES.map(t => (
              <button key={t.id} onClick={() => pick(t.id)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-panel text-left">
                <span className={clsx('w-10 h-10 rounded-full text-white text-xl flex items-center justify-center', t.color)}>
                  {t.icon}
                </span>
                <span className="font-medium text-navy">{t.label}</span>
              </button>
            ))}
            <button className="w-full text-muted py-2 text-sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {active && active !== 'photo' && (
        <CaptureModal
          section={active}
          onClose={() => { setActive(null); setOpen(false); }}
        />
      )}

      {active === 'photo' && (
        <PhotoModal
          files={photoFiles || []}
          onClose={() => { setActive(null); setOpen(false); setPhotoFiles(null); }}
        />
      )}

      <input ref={cameraRef} type="file" accept="image/*" capture="environment"
             className="hidden" onChange={onCameraPick} />
    </>
  );
}

const FIELD_SPEC = {
  wins: [
    { name: 'description', label: 'What was the win?', required: true, type: 'textarea' },
    { name: 'impact', label: 'Impact (optional)', type: 'text' },
  ],
  customer_challenges: [
    { name: 'customer', label: 'Customer', type: 'customer' },
    { name: 'issue', label: 'Issue', required: true, type: 'textarea' },
    { name: 'action_taken', label: 'Action taken (optional)', type: 'text' },
    { name: 'support_needed', label: 'Support needed (optional)', type: 'text' },
  ],
  new_opportunities: [
    { name: 'customer', label: 'Customer', type: 'customer' },
    { name: 'description', label: 'Opportunity', required: true, type: 'textarea' },
    { name: 'estimated_value', label: 'Estimated value (optional)', type: 'text', placeholder: '$' },
    { name: 'next_steps', label: 'Next steps (optional)', type: 'text' },
  ],
  high_alerts: [
    { name: 'account', label: 'Account', type: 'customer' },
    { name: 'issue', label: 'Issue', required: true, type: 'textarea' },
    { name: 'urgency', label: 'Urgency', type: 'select', options: ['Low', 'Medium', 'High'], default: 'High' },
    { name: 'recommended_action', label: 'Recommended action (optional)', type: 'text' },
  ],
};

const TITLES = {
  wins: '🏆 Log a win',
  customer_challenges: '⚠️ Flag a challenge',
  new_opportunities: '💰 New opportunity',
  high_alerts: '🚨 High alert',
};

function PhotoModal({ files, onClose }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [caption, setCaption] = useState('');
  const [customer, setCustomer] = useState('');
  const customers = useQuery({
    queryKey: ['customers', 'lookup'],
    queryFn: () => api.get('/customers').then(r => r.data.data),
  });
  const moreRef = useRef(null);
  const [allFiles, setAllFiles] = useState(files);

  const m = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      for (const f of allFiles) fd.append('files', f);
      if (caption.trim()) fd.append('caption', caption.trim());
      if (customer) {
        const match = customers.data?.find(c => c.name === customer);
        if (match) fd.append('customer_id', String(match.id));
      }
      const r = await api.post('/notes/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data.data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['notes'] });
      onClose();
      nav(`/notes/${d.note_id}`);
    },
  });

  function addMore(e) {
    const more = Array.from(e.target.files || []);
    if (more.length) setAllFiles([...allFiles, ...more]);
    e.target.value = '';
  }

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-panel"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold">📷 Photo</h3>
          <span className="text-xs text-muted">{allFiles.length} attached</span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {allFiles.map((f, i) => (
            <div key={i} className="relative">
              <img src={URL.createObjectURL(f)} alt="" className="w-full h-20 object-cover rounded" />
              <button onClick={() => setAllFiles(allFiles.filter((_, j) => j !== i))}
                      className="absolute top-0.5 right-0.5 bg-white/80 text-crimson text-xs rounded px-1">✕</button>
            </div>
          ))}
          <button onClick={() => moreRef.current?.click()}
                  className="h-20 border-2 border-dashed border-panelDark rounded text-2xl text-muted hover:bg-panel">+</button>
        </div>
        <input ref={moreRef} type="file" accept="image/*" capture="environment" multiple
               className="hidden" onChange={addMore} />

        <div>
          <label className="label">Caption (optional)</label>
          <textarea className="input min-h-[60px]" value={caption}
                    onChange={e => setCaption(e.target.value)}
                    placeholder="What's in the photo?" />
        </div>
        <div>
          <label className="label">Customer (optional)</label>
          <input className="input" list="qc-photo-cust" value={customer}
                 onChange={e => setCustomer(e.target.value)} placeholder="Type a name…" />
          <datalist id="qc-photo-cust">
            {customers.data?.map(c => <option key={c.id} value={c.name} />)}
          </datalist>
        </div>

        {m.isError && (
          <div className="text-crimson text-sm">
            {m.error?.response?.data?.error || 'Save failed'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!allFiles.length || m.isPending}
                  onClick={() => m.mutate()}>
            {m.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CaptureModal({ section, onClose }) {
  const qc = useQueryClient();
  const fields = FIELD_SPEC[section];
  const [form, setForm] = useState(
    Object.fromEntries(fields.map(f => [f.name, f.default || '']))
  );

  const customers = useQuery({
    queryKey: ['customers', 'lookup'],
    queryFn: () => api.get('/customers').then(r => r.data.data),
    enabled: fields.some(f => f.type === 'customer'),
  });

  const m = useMutation({
    mutationFn: (data) => api.post(`/recaps/current/${section}`, data).then(r => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recap', 'current'] });
      qc.invalidateQueries({ queryKey: ['recaps'] });
      onClose();
    },
  });

  const canSave = fields.every(f => !f.required || (form[f.name] || '').trim());

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-panel"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold">{TITLES[section]}</h3>
          <span className="text-xs text-muted">→ this week's recap</span>
        </div>

        {fields.map(f => (
          <div key={f.name}>
            <label className="label">{f.label}{f.required && <span className="text-crimson"> *</span>}</label>
            {f.type === 'textarea' && (
              <textarea className="input min-h-[80px]" placeholder={f.placeholder}
                value={form[f.name]} onChange={e => setForm({ ...form, [f.name]: e.target.value })}
                autoFocus={f.required} />
            )}
            {f.type === 'text' && (
              <input className="input" placeholder={f.placeholder}
                value={form[f.name]} onChange={e => setForm({ ...form, [f.name]: e.target.value })} />
            )}
            {f.type === 'select' && (
              <select className="input" value={form[f.name]}
                onChange={e => setForm({ ...form, [f.name]: e.target.value })}>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {f.type === 'customer' && (
              <input className="input" list={`cust-${f.name}`}
                placeholder="Type a name…"
                value={form[f.name]} onChange={e => setForm({ ...form, [f.name]: e.target.value })} />
            )}
            {f.type === 'customer' && (
              <datalist id={`cust-${f.name}`}>
                {customers.data?.map(c => <option key={c.id} value={c.name} />)}
              </datalist>
            )}
          </div>
        ))}

        {m.isError && (
          <div className="text-crimson text-sm">
            {m.error?.response?.data?.error || 'Save failed'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!canSave || m.isPending}
                  onClick={() => m.mutate(form)}>
            {m.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
